import {
  Box,
  FormHelperText,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

type IProps = TextFieldProps & {
  name: string;
  label?: string;
};

export const TwoFactorAuthInput: React.FC<IProps> = ({
                                                       name,
                                                       label,
                                                       ...rest
                                                     }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const inputRefs = React.useRef<(HTMLInputElement | null)[]>(
    Array(6).fill(null)
  );

  const fieldError = errors[name];
  const errorMessage = fieldError?.message as string | undefined;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {label && (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          <Typography
            component='label'
            variant='subtitle2'
            sx={{
              display: 'block',
              marginBottom: 1,
              fontWeight: 600,
              color: 'text.secondary',
            }}
          >
            {label}
          </Typography>
        </Box>
      )}

      <Controller
        name={name}
        control={control}
        defaultValue=''
        rules={{
          required: 'Dieses Feld ist erforderlich',
          pattern: {
            value: /^\d{6}$/,
            message:
              'Bitte alle 6 Ziffern des Authentifizierungsschlüssel eingeben',
          },
        }}
        render={({ field }) => {
          const codeArray = Array(6).fill('');
          const fieldValue = field.value ? field.value.toString() : '';

          for (let i = 0; i < fieldValue.length && i < 6; i++) {
            codeArray[i] = fieldValue[i];
          }

          const focusNext = (newCode: string[], startAt: number) => {
            for (let i = startAt; i < 6; i++) {
              if (newCode[i] === '') {
                inputRefs.current[i]?.focus();
                return;
              }
            }
            inputRefs.current[5]?.focus();
          };

          const applyDigitsFromIndex = (startIndex: number, raw: string) => {
            const digits = raw
              .replace(/\D/g, '')
              .split('')
              .slice(0, 6 - startIndex);

            if (!digits.length) return;

            const newCode = [...codeArray];
            let fillIndex = startIndex;

            for (const d of digits) {
              if (fillIndex >= 6) break;
              newCode[fillIndex] = d;
              fillIndex++;
            }

            field.onChange(newCode.join(''));
            focusNext(newCode, startIndex);
          };

          const handleChange = (index: number, newValue: string) => {
            // Wichtig: bei Mobile OTP Autofill kommt hier oft der komplette Code rein
            const rawDigits = (newValue || '').replace(/\D/g, '');

            if (rawDigits.length === 0) {
              const newCode = [...codeArray];
              newCode[index] = '';
              field.onChange(newCode.join(''));
              return;
            }

            // 1 Ziffer: normales Tippen
            if (rawDigits.length === 1) {
              const newCode = [...codeArray];
              newCode[index] = rawDigits;
              field.onChange(newCode.join(''));

              if (index < 5) {
                inputRefs.current[index + 1]?.focus();
              }
              return;
            }

            // >1 Ziffer: OTP Autofill / Tastatur-Vorschlag / schnelles Einfügen
            applyDigitsFromIndex(index, rawDigits);
          };

          const handleKeyDown = (
            index: number,
            e: React.KeyboardEvent<HTMLDivElement>
          ) => {
            if (e.key === 'Backspace') {
              e.preventDefault();

              if (codeArray[index] !== '') {
                const newCode = [...codeArray];
                newCode[index] = '';
                field.onChange(newCode.join(''));
              } else if (index > 0) {
                inputRefs.current[index - 1]?.focus();
              }
            }
          };

          const handlePaste = (
            index: number,
            event: React.ClipboardEvent<HTMLInputElement>
          ) => {
            // Nur reagieren wenn genau dieses Input aktiv ist
            if (document.activeElement !== inputRefs.current[index]) {
              return;
            }

            event.preventDefault();

            const pastedText = event.clipboardData?.getData('text') || '';
            applyDigitsFromIndex(index, pastedText);
          };

          return (
            <>
              <Box
                sx={{
                  display: 'flex',
                  gap: { xs: '6px', sm: '10px' },
                  justifyContent: 'center',
                }}
              >
                {codeArray.map((digit, i) => (
                  <TextField
                    key={i}
                    inputRef={el => (inputRefs.current[i] = el)}
                    value={digit}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleChange(i, e.target.value)
                    }
                    onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) =>
                      handleKeyDown(i, e)
                    }
                    onPaste={(e: React.ClipboardEvent<HTMLInputElement>) =>
                      handlePaste(i, e)
                    }
                    inputProps={{
                      pattern: '[0-9]*',
                      inputMode: 'numeric',
                      // WICHTIG: kein maxLength=1, sonst kappt Mobile Autofill den Code
                      autoComplete: i === 0 ? 'one-time-code' : 'off',
                      style: {
                        textAlign: 'center',
                        fontSize: '1.45rem',
                        fontWeight: 600,
                        padding: 0,
                      },
                    }}
                    variant='outlined'
                    error={!!fieldError}
                    sx={{
                      width: { xs: 44, sm: 52 },
                      '& .MuiOutlinedInput-root': {
                        height: { xs: 52, sm: 60 },
                        borderRadius: 2,
                      },
                    }}
                    {...rest}
                  />
                ))}
              </Box>

              {errorMessage && (
                <FormHelperText error sx={{ marginTop: 1, textAlign: 'center' }}>
                  {errorMessage}
                </FormHelperText>
              )}
            </>
          );
        }}
      />
    </Box>
  );
};